import { Box, TextField } from "@mui/material";
import { AutoCompleteSelector } from "../../../generic/components/auto-complete/auto-complete";
// oxlint-disable-next-line import/no-cycle -- Content Library modal and link form render each other at runtime (insert as link opens LinkForm, whose Content Library field opens the modal); breaking it needs dependency injection
import { ButtonFormContentLibraryLink } from "./button/button-form-content-library-link";
import { EmailCommunicationLink } from "./button/email-communication-link";
import { SurveySelectorDropdown } from "./survey/survey-selector-dropdown";

import type { UseSupabaseLoaderState } from "../../../../server/supabase/hooks/types";
import type { Asset } from "../../../assets/server/digital-asset-service";
import type { TagGroup } from "../../../merge-tag/components/types";
import type { MomentFormSurvey } from "../../../survey/server/supabase-survey-service";
import type { LinkType } from "./button/types";
import type { FC } from "react";
import type { FieldError } from "react-hook-form";

interface Props {
  linkOption: LinkType;
  value: string | null;
  onChange: (value: string) => void;
  onSelectedTitle?: (title: string) => void;
  assetsLoader?: UseSupabaseLoaderState<Asset[]> & { reload: () => void };
  surveyLoader: UseSupabaseLoaderState<MomentFormSurvey[]>;
  error?: FieldError;
  filterTags: (x: string) => TagGroup;
  urlLabel: string;
  urlPlaceholder?: string;
  useRawAssetPublicUrl?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

export const LinkTargetUrlField: FC<Props> = ({
  linkOption,
  value,
  onChange,
  onSelectedTitle,
  error,
  assetsLoader,
  filterTags,
  surveyLoader,
  urlLabel,
  urlPlaceholder = "https://www.example.com",
  useRawAssetPublicUrl = false,
  disabled,
  autoFocus,
}) => {
  switch (linkOption.type) {
    case "link_url":
      return (
        <AutoCompleteSelector
          defaultValue={value || "https://"}
          filterTags={filterTags}
          onChange={onChange}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              id="url"
              label={urlLabel}
              fullWidth
              variant="standard"
              value={value ?? ""}
              placeholder={urlPlaceholder}
              onChange={(x) => {
                params.onChange?.(x);
                onChange(x.target.value);
              }}
              helperText={error?.message}
              error={Boolean(error)}
              disabled={disabled}
              autoFocus={autoFocus}
            />
          )}
        />
      );

    case "link_lib":
      return assetsLoader ? (
        <Box sx={{ mt: 2 }}>
          <ButtonFormContentLibraryLink
            onUseAsLink={(url: string, title?: string) => {
              onChange(url);
              if (title) onSelectedTitle?.(title);
            }}
            onRemoveLink={() => {
              onChange("");
            }}
            formError={error}
            assetsLoader={assetsLoader}
            useRawAssetPublicUrl={useRawAssetPublicUrl}
          />
        </Box>
      ) : null;

    case "link_survey":
      return (
        <SurveySelectorDropdown
          onChange={onChange}
          surveyLoader={surveyLoader}
          error={error}
          id={linkOption.id}
          channel={linkOption.channel}
        />
      );

    case "link_email":
      return (
        <Box sx={{ mt: 2 }}>
          <EmailCommunicationLink
            onUseAsLink={(url, title) => {
              onChange(url);
              if (title) onSelectedTitle?.(title);
            }}
          />
        </Box>
      );
  }
  return undefined;
};
